import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import { RosterPilotLogo } from '../../assets/RosterPilotLogo';
import './AccountPausedScreen.css';

/**
 * Shown only if the server gave us no message at all. Deliberately generic -
 * the pause reason is server-owned copy (it may be a trial expiry, or a custom
 * message a system admin set for some other kind of suspension), so we must
 * never guess at it here.
 */
const FALLBACK_MESSAGE =
  'This account is currently paused. Please contact your administrator.';

const formatCheckedAt = (checkedAt: Date): string =>
  checkedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

interface AccountPausedScreenProps {
  /**
   * The pause copy to show. Defaults to the college organization's, from the session. A travel ball
   * program passes its own, since the session does not carry it.
   */
  message?: string | null;
  /** The program name under the heading. Defaults to the college organization's. */
  organizationName?: string | null;
  /**
   * What "Check again" does. Defaults to re-reading the profile, which is what clears a college
   * pause. It resolves while still paused and throws when the check itself failed.
   */
  onCheckAgain?: () => Promise<void>;
}

/**
 * Blocking screen rendered in place of the app for users whose organization has
 * been paused. The user stays signed in (Firebase-side), so signing out works.
 */
export const AccountPausedScreen = ({
  message: messageOverride,
  organizationName: organizationNameOverride,
  onCheckAgain,
}: AccountPausedScreenProps = {}) => {
  const { user, organizationPausedMessage, logout, loading, refreshProfile } = useAuth();
  const [signOutError, setSignOutError] = useState('');
  const [checkError, setCheckError] = useState('');
  const [checking, setChecking] = useState(false);
  // Set when a check completed and the organization was still paused. Without
  // this the button - the only action a blocked user has - looks broken on its
  // most common outcome.
  const [stillPausedAt, setStillPausedAt] = useState<Date | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const organizationName =
    (organizationNameOverride !== undefined
      ? organizationNameOverride?.trim()
      : user?.profile?.organizationName?.trim()) || null;
  const message =
    (messageOverride !== undefined ? messageOverride : organizationPausedMessage)?.trim() ||
    FALLBACK_MESSAGE;

  // This screen replaces the whole app, so move focus to its heading - otherwise
  // focus falls to <body> and nothing announces the change.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleSignOut = async () => {
    setSignOutError('');

    try {
      await logout();
    } catch {
      setSignOutError('Failed to sign out. Please try again.');
    }
  };

  const handleCheckAgain = async () => {
    setCheckError('');
    setStillPausedAt(null);
    setChecking(true);

    try {
      // If the organization has been resumed, this clears the paused state and
      // ProtectedRoute renders the app again.
      await (onCheckAgain ?? refreshProfile)();
      // Reaching here means this screen is still mounted, so the pause is still
      // in effect. Report the outcome explicitly.
      setStillPausedAt(new Date());
    } catch {
      setCheckError('Could not check your account status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="account-paused">
      <main className="account-paused__container">
        <div className="account-paused__logo">
          <RosterPilotLogo />
        </div>

        <div className="account-paused__card">
          <h1 className="account-paused__title" ref={headingRef} tabIndex={-1}>
            Account Paused
          </h1>

          {organizationName && (
            <p className="account-paused__org" data-testid="account-paused-org">
              {organizationName}
            </p>
          )}

          {/*
            No live region here on purpose: this copy is present at mount, and a
            live region never announces its initial content. The heading above
            owns the announcement on arrival; the regions below own the updates.
          */}
          <p className="account-paused__message" data-testid="account-paused-message">
            {message}
          </p>

          {/*
            The live roles belong on these - the messages that appear in
            response to a user action. Failures are assertive (role="alert"),
            the "still paused" outcome is polite (role="status").
          */}
          {checkError && (
            <p
              className="account-paused__error"
              role="alert"
              data-testid="account-paused-check-error"
            >
              {checkError}
            </p>
          )}

          {signOutError && (
            <p
              className="account-paused__error"
              role="alert"
              data-testid="account-paused-sign-out-error"
            >
              {signOutError}
            </p>
          )}

          {stillPausedAt && !checkError && (
            <p
              className="account-paused__result"
              role="status"
              data-testid="account-paused-check-result"
            >
              Still paused - last checked at {formatCheckedAt(stillPausedAt)}
            </p>
          )}

          <div className="account-paused__actions">
            <Button
              type="button"
              fullWidth
              loading={checking}
              onClick={handleCheckAgain}
              data-testid="account-paused-check-again"
            >
              Check Again
            </Button>

            <Button
              type="button"
              variant="secondary"
              fullWidth
              loading={loading}
              onClick={handleSignOut}
              data-testid="account-paused-sign-out"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};
